import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScanLine, PenLine, Loader2, Radio, MapPin, QrCode, Copy, Check, Gauge, Cloud, Zap } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { scanNfc, isNativeApp } from "@/lib/nativeNfc";
import WelcomeOnboarding from "@/components/WelcomeOnboarding";
import CardScanDialog from "@/components/CardScanDialog";
import VoiceDebriefCard from "@/components/VoiceDebriefCard";
import PriorityLeads from "@/components/PriorityLeads";
import RotatingGreeting from "@/components/RotatingGreeting";
import MeetingHubCard from "@/components/MeetingHubCard";
import CaptureSuccess from "@/components/CaptureSuccess";
import { useMode } from "@/lib/mode";
import LeaderboardCard from "@/components/LeaderboardCard";
import { useOffline } from "@/hooks/useOffline";

const PRESET_SHOWS = ["Le Bourget 2026", "VivaTech 2026", "CES Paris 2026", "SIDO Lyon 2026", "Global Industrie 2026"];

export default function CaptureHubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(() => { const v = localStorage.getItem("klt_event") || ""; return v === "Le Bourget 2026" ? "" : v; });
  const [ocrOpen, setOcrOpen] = useState(false);
  const [nfcOpen, setNfcOpen] = useState(false);
  const [nfcId, setNfcId] = useState("");
  const [nfcLoading, setNfcLoading] = useState(false);
  const [nfcScanning, setNfcScanning] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [captureUrl, setCaptureUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const [success, setSuccess] = useState(null);
  const [mode] = useMode();
  const [leads, setLeads] = useState(() => { try { return JSON.parse(localStorage.getItem("klt_leads_cache") || "[]"); } catch { return []; } });
  const [rdvIds, setRdvIds] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem("klt_rdv_cache") || "[]")); } catch { return new Set(); } });
  const { online, pending } = useOffline();

  useEffect(() => {
    const load = () => {
      api.get("/leads").then(({ data }) => {
        const list = Array.isArray(data) ? data : data.leads || [];
        setLeads(list);
        try { localStorage.setItem("klt_leads_cache", JSON.stringify(list.slice(0, 200).map((l) => ({ id: l.id, createdAt: l.createdAt, eventName: l.eventName, contactInfo: l.contactInfo, aiClassification: l.aiClassification, followUpSequence: l.followUpSequence })))); } catch { /* quota */ }
      }).catch(() => {});
      api.get("/appointments").then(({ data }) => {
        const ids = (data || []).map((a) => a.leadId).filter(Boolean);
        setRdvIds(new Set(ids));
        localStorage.setItem("klt_rdv_cache", JSON.stringify(ids));
      }).catch(() => {});
    };
    load();
    window.addEventListener("birdie:changed", load);
    return () => window.removeEventListener("birdie:changed", load);
  }, []);
  useEffect(() => {
    const st = location.state || {};
    if (st.justCaptured) { setSuccess(st.justCaptured); navigate(".", { replace: true, state: {} }); }
    else if (st.openScan) { setOcrOpen(true); navigate(".", { replace: true, state: {} }); }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayCount = leads.filter((l) => (l.createdAt || "").slice(0, 10) === todayStr).length + pending;
  const recent = [...leads].sort((x, y) => (y.createdAt || "").localeCompare(x.createdAt || "")).slice(0, 5);

  const openQr = async () => {
    setQrOpen(true);
    if (captureUrl) return;
    try {
      const { data } = await api.get("/capture/link");
      setCaptureUrl(`${window.location.origin}/card/${data.slug}`);
    } catch {
      toast.error("Impossible de générer votre lien de capture");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(captureUrl);
      setCopied(true);
      toast.success("Lien copié ✨");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Mes coordonnées Kallitag", url: captureUrl }); } catch { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  useEffect(() => {
    if (localStorage.getItem("klt_onboarded")) return;
    const seen = sessionStorage.getItem("klt_splash_seen");
    const t = setTimeout(() => setShowWelcome(true), seen ? 250 : 5100);
    return () => clearTimeout(t);
  }, []);

  const closeWelcome = () => {
    localStorage.setItem("klt_onboarded", "1");
    setShowWelcome(false);
  };

  const saveEvent = (v) => {
    setEvent(v);
    localStorage.setItem("klt_event", v);
  };

  const requireEvent = () => {
    if (!event.trim()) {
      toast.error("Définissez d'abord le salon en cours.");
      return false;
    }
    return true;
  };

  const goOfflinePhoto = (file) => {
    setOcrOpen(false);
    toast("📴 Hors ligne — photo enregistrée, OCR au retour du réseau");
    navigate("/lead/new", { state: { captureMethod: "OCR_CARD", eventName: event, pendingPhotoBlob: file, pendingOcr: true } });
  };

  const resolveNfc = async () => {
    if (!nfcId.trim()) return;
    setNfcLoading(true);
    try {
      const { data } = await api.post("/nfc-resolve", { cardIdOrUrl: nfcId.trim() });
      setNfcOpen(false);
      setNfcId("");
      if (data.matched) {
        toast.success(data.source ? "Profil Kallitag importé ✨" : "Compte Kallitag associé 🔗");
      } else {
        toast("Carte non reconnue — fiche pré-remplie à compléter", { icon: "📝" });
      }
      navigate("/lead/new", { state: { contactInfo: data.contactInfo, captureMethod: "NFC_KALLITAG", eventName: event } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la lecture NFC");
    } finally {
      setNfcLoading(false);
    }
  };

  const nfcSupported = isNativeApp() || (typeof window !== "undefined" && "NDEFReader" in window);

  const scanWebNfc = async () => {
    try {
      setNfcScanning(true);
      const value = await scanNfc();
      setNfcScanning(false);
      if (value) {
        setNfcId(value);
        toast.success("Puce NFC lue ✨");
      } else {
        toast.error("Aucune donnée lue, réessayez.");
      }
    } catch (e) {
      setNfcScanning(false);
      if (e?.message === "nfc-unsupported") {
        toast.error("Scan direct non autorisé ici (iPhone). Approchez la carte, copiez le lien qui s'ouvre et collez-le ci-dessous.");
      } else {
        toast.error("Lecture NFC échouée, réessayez.");
      }
    }
  };

  const tiles = [
    { key: "scan", testid: "quick-scan", icon: ScanLine, title: "Capture éclair", desc: "OCR carte de visite & scan NFC", onClick: () => requireEvent() && setOcrOpen(true) },
    { key: "score", testid: "quick-score", icon: Gauge, title: "Probabilité de signature", desc: "Priorisation IA de vos prospects", onClick: () => document.querySelector("[data-testid=priority-leads]")?.scrollIntoView({ behavior: "smooth" }) },
    { key: "crm", testid: "quick-crm", icon: Cloud, title: "Sync CRM", desc: "HubSpot, Salesforce & Pipedrive", onClick: () => navigate("/dashboard") },
    { key: "qr", testid: "quick-qr", icon: QrCode, title: "Ma carte digitale", desc: "QR code à faire scanner", onClick: openQr },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-5">
      {showWelcome && <WelcomeOnboarding onClose={closeWelcome} />}
      {success && <CaptureSuccess lead={success} onClose={() => setSuccess(null)} />}
      <div className="text-center pt-2 klt-fade-up" data-testid="klt-hero">
        <div data-testid="klt-greeting"><RotatingGreeting name={user?.name?.split(" ")[0] || ""} className="font-heading text-5xl sm:text-6xl font-extrabold tracking-tight" /></div>
        <div className="mt-3 inline-flex items-center gap-2" data-testid="klt-title">
          <span className="w-7 h-7 rounded-lg klt-logo-blue flex items-center justify-center"><Zap className="w-4 h-4 text-white" fill="currentColor" /></span>
          <span className="font-heading font-extrabold text-sm">KLT</span>
          <span className="text-[10px] font-semibold tracking-[0.35em] text-[#60A5FA]">LEAD CAPTURE</span>
        </div>
        <p className="text-sm text-muted-foreground italic mt-2 max-w-md mx-auto" data-testid="klt-quote">« La ressource la plus précieuse dont nous disposons tous est le temps. » — Steve Jobs</p>
      </div>
      {!online && <p className="text-xs font-semibold text-slate-300 bg-slate-500/15 rounded-xl px-3 py-2" data-testid="leads-offline-note">📴 Hors ligne — données du dernier passage + {pending} capture{pending > 1 ? "s" : ""} en attente</p>}

      <div className="grid grid-cols-2 gap-3" data-testid="quick-actions">
        <VoiceDebriefCard />
        <MeetingHubCard />
        {tiles.map((q, i) => { const Icon = q.icon; return (
          <button key={q.key} data-testid={q.testid} onClick={q.onClick} style={{ animationDelay: `${(i + 1) * 70}ms` }}
            className="klt-fade-up klt-glow-card rounded-3xl p-4 text-left flex flex-col sm:flex-row sm:items-center gap-3 hover:-translate-y-0.5 transition-transform duration-300">
            <div className="w-11 h-11 rounded-2xl bg-[#3B82F6]/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#60A5FA]" /></div>
            <div className="min-w-0"><p className="font-heading font-bold text-[15px] leading-tight">{q.title}</p><p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{q.desc}</p></div>
          </button>); })}
      </div>

      <div className="klt-glow-card rounded-2xl p-3 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
        <Input data-testid="tradeshow-selector" value={event} onChange={(e) => saveEvent(e.target.value)} placeholder="Salon / événement en cours" className="h-9 border-0 bg-transparent font-semibold focus-visible:ring-0" />
        <Button variant="ghost" size="sm" onClick={() => requireEvent() && setNfcOpen(true)} data-testid="nfc-open-button" className="rounded-xl shrink-0 px-2">NFC</Button>
        <Button variant="ghost" size="sm" onClick={() => requireEvent() && navigate("/lead/new", { state: { captureMethod: "MANUAL", eventName: event } })} data-testid="manual-entry-button" className="rounded-xl shrink-0"><PenLine className="w-4 h-4 mr-1" /> Saisie</Button>
      </div>

      <PriorityLeads leads={leads} />
      {online && <LeaderboardCard event={event} />}

      {/* OCR dialog — recto + verso */}
      <CardScanDialog
        open={ocrOpen}
        onClose={() => setOcrOpen(false)}
        onScanned={(ci) => navigate("/lead/new", { state: { contactInfo: ci, captureMethod: "OCR_CARD", eventName: event } })}
        onOffline={goOfflinePhoto}
      />

      {/* Reverse capture QR dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ma carte digitale</DialogTitle>
            <DialogDescription>Faites scanner ce QR : le prospect voit votre carte, enregistre votre contact et peut vous laisser ses coordonnées.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-2">
            {captureUrl ? (
              <div className="bg-white p-4 rounded-2xl border border-border" data-testid="qr-canvas">
                <QRCodeCanvas value={captureUrl} size={200} level="M" includeMargin={false} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[232px]"><Loader2 className="w-6 h-6 klt-spin text-primary" /></div>
            )}
            {captureUrl && (
              <div className="w-full mt-4 space-y-2">
                <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2">
                  <span className="text-xs text-muted-foreground truncate flex-1" data-testid="capture-link-text">{captureUrl}</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 rounded-lg" onClick={copyLink} data-testid="copy-capture-link-button">
                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <Button onClick={shareLink} data-testid="share-capture-link-button" className="w-full rounded-xl">Partager mon lien</Button>
                <Button variant="outline" onClick={() => navigate("/ma-carte")} data-testid="edit-card-button" className="w-full rounded-xl mt-2">Personnaliser ma carte</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* NFC dialog */}
      <Dialog open={nfcOpen} onOpenChange={setNfcOpen}>
        <DialogContent data-testid="nfc-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Associer une carte NFC Kallitag</DialogTitle>
            <DialogDescription>Approchez la carte ou saisissez son identifiant / URL de profil.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <div className="relative w-24 h-24 rounded-full bg-violet-500/10 flex items-center justify-center">
              <div className="klt-nfc-pulse absolute inset-0" />
              <Radio className="w-10 h-10 text-violet-500" />
            </div>
          </div>
          {nfcSupported ? (
            <>
              <Button onClick={scanWebNfc} disabled={nfcScanning} variant="outline" data-testid="webnfc-scan-button" className="w-full rounded-xl border-violet-300 dark:border-violet-800">
                {nfcScanning ? <><Loader2 className="w-4 h-4 klt-spin mr-2" /> En attente d'une puce…</> : <><Radio className="w-4 h-4 mr-2" /> Scanner la puce (WebNFC)</>}
              </Button>
              <div className="text-center text-xs text-muted-foreground my-1">ou collez le lien de la carte</div>
            </>
          ) : (
            <div className="rounded-xl border border-violet-300/50 bg-violet-500/5 p-3 text-sm" data-testid="nfc-ios-hint">
              <p className="font-medium text-foreground">Scan direct indisponible sur iPhone</p>
              <p className="text-muted-foreground mt-1 leading-relaxed">
                Apple n'autorise pas la lecture NFC dans une app web. Approchez la carte de votre téléphone : un lien Kallitag s'ouvre automatiquement.
                <b> Copiez ce lien</b>, revenez ici et collez-le ci-dessous pour importer le contact.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{nfcSupported ? "Identifiant ou lien de la carte" : "Collez le lien de la carte Kallitag"}</Label>
            <Input
              data-testid="nfc-id-input"
              value={nfcId}
              onChange={(e) => setNfcId(e.target.value)}
              placeholder="https://kallitag.com/c/KLT-8842"
              onKeyDown={(e) => e.key === "Enter" && resolveNfc()}
            />
            <p className="text-xs text-muted-foreground">Démo : essayez KLT-8842, KLT-2291 ou KLT-5510</p>
          </div>
          <Button onClick={resolveNfc} disabled={nfcLoading} data-testid="nfc-resolve-button" className="w-full rounded-xl">
            {nfcLoading ? <Loader2 className="w-4 h-4 klt-spin" /> : "Importer le compte Kallitag"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
